import {useMemo, useState} from "react";

// A named day (SiteStatMarker), drawn as a line at the start of the bar it falls in.
export interface ChartMarker {
    index: number;
    name: string;
}

export interface ChartPoint {
    // What the bar is, for the tooltip and the table ("Oct 12", "Week of Oct 6").
    label: string;
    // Shorter, for the axis.
    tick: string;
    value: number;
}

const WIDTH = 800;
const HEIGHT = 220;
const PAD = { top: 12, right: 8, bottom: 24, left: 40 };
const GAP = 2;

// 0, and up to four round steps to a little above max.
function ticks(max: number): number[] {
    if (max <= 0) {
        return [0, 1];
    }
    const rough = max / 4;
    const power = 10 ** Math.floor(Math.log10(rough));
    const step = [1, 2, 5, 10].map(x => x * power).find(x => x >= rough) ?? rough;
    const result: number[] = [];
    for (let value = 0; value < max + step; value += step) {
        result.push(value);
    }
    return result;
}

// A bar with its top corners rounded (4px, less on narrow bars) and its foot square on the baseline.
function barPath(x: number, y: number, width: number, height: number): string {
    const r = Math.min(4, width / 2, height);
    return `M${x},${y + height}V${y + r}Q${x},${y} ${x + r},${y}H${x + width - r}Q${x + width},${y} ${x + width},${y + r}V${y + height}Z`;
}

// One series of counts over time as bars, named by the title beside it (so no legend), with a tooltip on hover, and the
// markers' days as labeled lines. The counts are also in the table under it, for screen readers and exact values. Colors: site.css .stats-chart.
export function VisitsChart({points, unit, markers = []}: { points: ChartPoint[], unit: string, markers?: ChartMarker[] }) {
    const [hover, setHover] = useState<number>();
    const scale = useMemo(() => ticks(Math.max(0, ...points.map(x => x.value))), [points]);
    const top = scale[scale.length - 1];
    const plotWidth = WIDTH - PAD.left - PAD.right;
    const plotHeight = HEIGHT - PAD.top - PAD.bottom;
    const slot = plotWidth / Math.max(1, points.length);
    const barWidth = Math.max(1, slot - GAP);
    const y = (value: number) => PAD.top + plotHeight - (value / top) * plotHeight;
    const tickEvery = Math.max(1, Math.ceil(points.length / 6));
    const shown = hover !== undefined ? points[hover] : undefined;

    return (
        <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className="stats-chart" role="img" aria-label={`${unit} over time; the table below has each value`}
             onMouseLeave={() => setHover(undefined)}>
            {scale.map(value => (
                <g key={value}>
                    <line x1={PAD.left} x2={WIDTH - PAD.right} y1={y(value)} y2={y(value)} className={value === 0 ? 'stats-chart-baseline' : 'stats-chart-grid'}/>
                    <text x={PAD.left - 6} y={y(value)} dy="0.32em" textAnchor="end" className="stats-chart-label">{value}</text>
                </g>
            ))}
            {points.map((point, i) => {
                const x = PAD.left + i * slot + GAP / 2;
                const height = (point.value / top) * plotHeight;
                return (
                    <g key={point.label}>
                        {height > 0 && <path d={barPath(x, y(point.value), barWidth, height)} className={`stats-chart-bar${hover === i ? ' active' : ''}`}/>}
                        {i % tickEvery === 0 && <text x={x + barWidth / 2} y={HEIGHT - 6} textAnchor="middle" className="stats-chart-label">{point.tick}</text>}
                        {/* The whole column is the hit target, not just the bar. */}
                        <rect x={PAD.left + i * slot} y={PAD.top} width={slot} height={plotHeight} className="stats-chart-hit" onMouseEnter={() => setHover(i)}/>
                    </g>
                );
            })}
            {markers.map(marker => {
                const x = PAD.left + marker.index * slot;
                return (
                    <g key={`${marker.index}-${marker.name}`} className="stats-chart-marker" pointerEvents="none">
                        <line x1={x} x2={x} y1={PAD.top} y2={PAD.top + plotHeight}/>
                        <text x={x + 4} y={PAD.top + 8}>{marker.name.length > 24 ? `${marker.name.substring(0, 23)}…` : marker.name}</text>
                    </g>
                );
            })}
            {shown && hover !== undefined && (() => {
                const text = `${shown.label}: ${shown.value} ${unit}`;
                const boxWidth = text.length * 7 + 16;
                const center = PAD.left + hover * slot + slot / 2;
                const boxX = Math.min(Math.max(PAD.left, center - boxWidth / 2), WIDTH - PAD.right - boxWidth);
                const boxY = Math.max(0, y(shown.value) - 34);
                return (
                    <g className="stats-chart-tooltip" pointerEvents="none">
                        <rect x={boxX} y={boxY} width={boxWidth} height={26} rx={4}/>
                        <text x={boxX + 8} y={boxY + 13} dy="0.32em">{text}</text>
                    </g>
                );
            })()}
        </svg>
    );
}
